import React from 'react';
import { useWatch } from 'react-hook-form';
import { useMutation } from '@tanstack/react-query';
import { Key } from 'lucide-react';
import type { ApiKeyCreate, IssuedApiKey, OrgMemberSummary } from '../../generated/api/types.gen.js';
import { zApiKeyCreate } from '../../generated/api/zod.gen.js';
import { createApiKeyMutation } from '../../generated/api/@tanstack/react-query.gen.js';
import { Button, Modal } from '../../components/ui/index.js';
import {
  CheckboxFormField,
  Form,
  MultiSelectFormField,
  SelectFormField,
  SubmitButton,
  TextFormField,
  useZodForm,
} from '../../components/form/index.js';
import { issueApiKeyBody } from './issue-api-key-body.js';

interface IssueApiKeyModalProps {
  open: boolean;
  onClose: () => void;
  onIssued: (issued: IssuedApiKey) => void;
  /** Members of the organization: who a personal key may act as. */
  members: OrgMemberSummary[];
  /** The permission keys a personal key may be narrowed to (generated server-side from the entity skills). */
  grantable: string[];
  currentUserId: number | null;
}

const SCOPE_OPTIONS = [
  { value: 'worker_sdk', label: 'Runtime', description: 'Your app invokes agents and workflows (Worker SDK)' },
  { value: 'service_account', label: 'Deployment', description: 'Build time: deploy schema and code' },
  { value: 'user', label: 'Personal', description: 'An external AI client acting as one person' },
];

function memberLabel(member: OrgMemberSummary, currentUserId: number | null): string {
  const name = member.fullName ?? member.username ?? member.email;
  return member.id === currentUserId ? `${name} (you)` : name;
}

/** Issue a key. Runtime and deployment keys need a name and environment; personal keys also pick the person and grants. */
export const IssueApiKeyModal: React.FC<IssueApiKeyModalProps> = ({
  open,
  onClose,
  onIssued,
  members,
  grantable,
  currentUserId,
}) => {
  const issue = useMutation(createApiKeyMutation());
  const form = useZodForm(zApiKeyCreate, {
    defaultValues: {
      name: '',
      scope: 'worker_sdk',
      environmentSlug: 'prod',
      userId: currentUserId ?? undefined,
      metadata: { grants: [], dataAccessEnabled: false },
    },
  });
  const scope = useWatch({ control: form.control, name: 'scope' });

  const close = () => {
    form.reset();
    onClose();
  };

  const submit = async (values: ApiKeyCreate) => {
    const issued = await issue.mutateAsync({ body: issueApiKeyBody(values) });
    onIssued(issued);
    close();
  };

  return (
    <Modal open={open} onClose={close} title="Issue API key" titleIcon={<Key size={16} />} maxWidth="lg">
      <Form form={form} onSubmit={submit}>
        <TextFormField
          name="name"
          label="Name"
          placeholder="prod-runtime"
          description="Shown in lists and audit logs next to the key prefix."
          required
        />
        <SelectFormField name="scope" label="Scope" options={SCOPE_OPTIONS} required />

        {scope === 'user' && (
          <>
            <SelectFormField
              name="userId"
              label="Acts as"
              options={members.map((member) => ({ value: member.id, label: memberLabel(member, currentUserId) }))}
              description="The key does exactly what this person can do here and stops working when they are deactivated."
              required
            />
            <MultiSelectFormField
              name="metadata.grants"
              label="Narrow to (optional)"
              options={grantable.map((grant) => ({ value: grant, label: grant }))}
              description="Leave empty to give the key every permission the person holds. A permission they do not hold is refused."
              placeholder="All of the person's permissions"
            />
            <CheckboxFormField
              name="metadata.dataAccessEnabled"
              label="Market data access"
              description="Lets the key read market data: the data tools, the market-data coverage, and the routes another Conflui installation's workers use to reach it. Off by default."
            />
          </>
        )}

        <TextFormField
          name="environmentSlug"
          label="Environment"
          placeholder="prod"
          description="Embedded in the key (for example ck_live_prod_…) so audit logs show the environment without exposing the secret."
          required
        />

        <div className="flex justify-end gap-2">
          <Button type="button" variant="ghost" onClick={close}>
            Cancel
          </Button>
          <SubmitButton>Issue key</SubmitButton>
        </div>
      </Form>
    </Modal>
  );
};
